import { useState, useEffect, useRef } from "react";
import type { User } from "firebase/auth";
import { getWorkspaceMembership } from "../lib/auth";
import { CopyInstructionsButton } from "./CopyInstructionsButton";
import { buildWorkspaceSetupInstructions } from "../lib/copyInstructions";

export function deriveWorkspaceName(user: User): string {
  if (user.displayName) return user.displayName;
  if (user.email) return user.email.split("@")[0];
  return "My Workspace";
}

export function WorkspaceCreation({
  user,
  onCreated,
  onSignOut,
  onShowJoin,
  onCancel,
  workspaceNameOverride,
  embedded,
  checkExistingMembership,
}: {
  user: User;
  /** track:workspace-creation-idempotency-fix — role is only passed when
   * checkExistingMembership routed into a pre-existing membership instead
   * of creating a new one; omitted (caller should default to "lead") for
   * every real creation, since creating a workspace always makes you its
   * lead. Getting this right matters — a partner routed into their
   * existing membership must not get silently promoted to lead. */
  onCreated: (workspaceId: string, role?: "lead" | "partner") => void;
  onSignOut: () => void;
  /** track:multi-workspace-membership Phase 3 — lets someone who landed
   * here (the default first-time gate) switch to JoinWorkspace instead,
   * e.g. because they actually have an invite code for an existing team
   * workspace rather than wanting a brand-new one. Omitted entirely by
   * the Phase 4 "create another workspace" modal usage, where it isn't
   * relevant — the link only renders when this is provided. */
  onShowJoin?: () => void;
  /** track:multi-workspace-membership Phase 4 — lets the "create another
   * workspace" modal usage be dismissed without provisioning anything.
   * Not used by the original first-time gate call site (there is nothing
   * to cancel back to before a membership exists). */
  onCancel?: () => void;
  /** track:multi-workspace-membership Phase 4 — the Phase 4 modal usage
   * passes a name pre-disambiguated against the account's existing
   * workspaces (see ProfilePage.tsx), since deriveWorkspaceName() alone
   * would otherwise give every self-created workspace for the same
   * account the identical default name. */
  workspaceNameOverride?: string;
  /** track:multi-workspace-membership Phase 4 — renders just the inner
   * card, without the fixed h-screen/w-screen full-viewport wrapper, for
   * reuse inside ProfilePage's "create another workspace" modal shell
   * (which supplies its own fixed overlay + centering). */
  embedded?: boolean;
  /** track:workspace-creation-idempotency-fix — when true, checks for an
   * existing confirmed membership immediately before provisioning and
   * routes into it instead of creating a duplicate. Closes the race where
   * App.tsx's own membership resolution briefly returns null (transient
   * Firestore latency, or a re-render before the real query settles),
   * mounting this component — and thus auto-provisioning a brand-new
   * workspace — for an account that already has a real one. Root cause of
   * 133 duplicate workspaces found 2026-09-01 (Team Log seq #41285).
   *
   * Only set by App.tsx's first-time gate call site. The Phase 4 "create
   * another workspace" modal (ProfilePage.tsx) deliberately never sets
   * this — creating an additional workspace there is the explicit point,
   * not a bug to guard against. */
  checkExistingMembership?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  // track:workspace-key-onboarding (decision #48248 outcome 6) — workspace
  // creation now mints the creator's own PERSONAL daemon key
  // (createOrRegenerateMemberKey, server/modules/team/workspaceService.ts),
  // shown once, never re-derivable. This used to be discarded here (only
  // `workspaceId` was ever read off the response), leaving a new self-serve
  // user with no way to ever obtain the credential `relay install --secret`
  // needs. Held here until the user confirms they've copied it, instead of
  // calling onCreated() immediately.
  const [provisioned, setProvisioned] = useState<{ workspaceId: string; mcpUrl: string; bearerToken: string } | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  // Provisioning below fires immediately on mount (no confirmation step,
  // by existing design, for the common single-workspace case). If the
  // user backs out (via "have an invite code instead" or Cancel) while
  // that request is still in flight, we must not let its resolution call
  // onCreated() afterward — that would silently switch them into the
  // just-auto-created workspace instead of what they actually meant to do.
  const cancelledRef = useRef(false);

  useEffect(() => {
    async function provision() {
      try {
        if (checkExistingMembership && user.email) {
          const existing = await getWorkspaceMembership(user.email);
          if (cancelledRef.current) return;
          if (existing) {
            onCreated(existing.workspaceId, existing.role);
            return;
          }
        }

        const ownerName = deriveWorkspaceName(user);
        const workspaceName = workspaceNameOverride ?? ownerName;

        const idToken = await user.getIdToken();
        const res = await fetch("/api/workspaces/create", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({ ownerName, workspaceName }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `Server error ${res.status}`);
        }

        const { workspaceId, mcpUrl, bearerToken } = await res.json();
        if (cancelledRef.current) return;
        if (mcpUrl && bearerToken) {
          setProvisioned({ workspaceId, mcpUrl, bearerToken });
        } else {
          // Defensive fallback only — createWorkspace() always returns both
          // today. Never strand the user on a dead screen if that changes.
          onCreated(workspaceId);
        }
      } catch (e) {
        if (cancelledRef.current) return;
        setError(
          e instanceof Error ? e.message : "Failed to create workspace. Please try again."
        );
      }
    }

    provision();
  }, [user, onCreated, workspaceNameOverride, checkExistingMembership]);

  function handleShowJoin() {
    cancelledRef.current = true;
    onShowJoin?.();
  }

  function handleCancel() {
    cancelledRef.current = true;
    onCancel?.();
  }

  function handleCopyUrl() {
    if (!provisioned) return;
    navigator.clipboard.writeText(provisioned.mcpUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  }

  function handleCopyKey() {
    if (!provisioned) return;
    navigator.clipboard.writeText(provisioned.bearerToken);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  }

  function handleContinue() {
    if (!provisioned) return;
    onCreated(provisioned.workspaceId);
  }

  const cardStyle: React.CSSProperties = {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    maxWidth: 420,
    width: "100%",
  };

  const linkButtonStyle: React.CSSProperties = {
    background: "transparent",
    color: "var(--accent)",
    border: 0,
    padding: "4px 0",
    fontSize: 11,
    cursor: "pointer",
    fontFamily: "inherit",
  };

  const monoBoxStyle: React.CSSProperties = {
    width: "100%",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    wordBreak: "break-all",
    padding: "8px 10px",
    border: "1px solid var(--border)",
    borderRadius: 6,
    color: "var(--text)",
    boxSizing: "border-box",
  };

  const smallBtnStyle: React.CSSProperties = {
    background: "var(--accent)",
    color: "#fff",
    border: 0,
    borderRadius: 6,
    padding: "6px 14px",
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
  };

  const content = provisioned ? (
    <div className="flex flex-col items-center gap-3 px-6 py-10" style={cardStyle}>
      <span className="font-mono text-lg lowercase tracking-tight" style={{ color: "var(--text)" }}>
        relay
      </span>
      <div className="text-sm font-medium" style={{ color: "var(--text)" }}>
        Your workspace is ready
      </div>
      <div style={{ fontSize: 12, color: "var(--tag-blocker)", textAlign: "center", fontWeight: 600 }}>
        This is your personal daemon key — copy it now, it won't be shown again.
      </div>

      <div style={{ width: "100%" }}>
        <div className="text-xs" style={{ color: "var(--text2)", marginBottom: 4 }}>
          MCP URL — paste into Claude.ai (Settings → Integrations → Add MCP Server) or another AI advisor:
        </div>
        <div style={monoBoxStyle}>{provisioned.mcpUrl}</div>
        <button onClick={handleCopyUrl} style={{ ...smallBtnStyle, marginTop: 6 }}>
          {copiedUrl ? "✓ Copied!" : "Copy URL"}
        </button>
      </div>

      <div style={{ width: "100%" }}>
        <div className="text-xs" style={{ color: "var(--text2)", marginBottom: 4 }}>
          Key only — use with <code>relay install --secret &lt;key&gt;</code> to install the local daemon:
        </div>
        <div style={monoBoxStyle}>{provisioned.bearerToken}</div>
        <button onClick={handleCopyKey} style={{ ...smallBtnStyle, marginTop: 6 }}>
          {copiedKey ? "✓ Copied!" : "Copy key"}
        </button>
      </div>

      <div className="text-xs text-center" style={{ color: "var(--text2)" }}>
        This key is yours alone. If you lose it, you can generate a new one anytime from Team → My daemon key — but the old one stops working immediately, so update every computer using it.
      </div>

      {/* track:workspace-key-onboarding (decision #48253) — the copied text
          is a self-contained walkthrough (goal, prerequisites, numbered
          OS-aware steps, expected result, verification, troubleshooting,
          safety) for an AI assistant to read and use to guide the person
          through the rest of setup. It never contains this key or URL —
          see src/lib/copyInstructions.ts's own header comment for why. */}
      <CopyInstructionsButton text={buildWorkspaceSetupInstructions()} />

      <button
        onClick={handleContinue}
        style={{
          background: "var(--accent)",
          color: "#fff",
          border: 0,
          borderRadius: 6,
          padding: "8px 18px",
          fontSize: 13,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        I've copied it — continue
      </button>
    </div>
  ) : error ? (
    <div className="flex flex-col items-center gap-4 px-6 py-10" style={cardStyle}>
      <span className="font-mono text-lg lowercase tracking-tight" style={{ color: "var(--text)" }}>
        relay
      </span>
      <div style={{ fontSize: 13, color: "var(--tag-blocker)", textAlign: "center", marginBottom: 8 }}>
        {error}
      </div>
      <button
        onClick={onSignOut}
        style={{
          background: "var(--accent)",
          color: "#fff",
          border: 0,
          borderRadius: 6,
          padding: "8px 18px",
          fontSize: 13,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        Sign out and try again
      </button>
      {onCancel && (
        <button onClick={handleCancel} style={linkButtonStyle}>
          Cancel
        </button>
      )}
    </div>
  ) : (
    <div className="flex flex-col items-center gap-4 px-6 py-10" style={cardStyle}>
      <span className="font-mono text-lg lowercase tracking-tight" style={{ color: "var(--text)" }}>
        relay
      </span>
      <div className="text-sm font-medium" style={{ color: "var(--text)" }}>
        Setting up your workspace…
      </div>
      <div className="text-xs text-center" style={{ color: "var(--text2)" }}>
        Signed in as{" "}
        <span style={{ fontFamily: "var(--font-mono)" }}>{user.email}</span>
      </div>
      {onShowJoin && (
        <button onClick={handleShowJoin} style={linkButtonStyle}>
          Have an invite code instead?
        </button>
      )}
      {onCancel && (
        <button onClick={handleCancel} style={linkButtonStyle}>
          Cancel
        </button>
      )}
    </div>
  );

  if (embedded) return content;

  return (
    <div
      className="h-screen w-screen flex items-center justify-center"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      {content}
    </div>
  );
}
